import apiClient from '@/config/api';
import {
  GenerateReferralLinkRequest,
  GenerateReferralLinkResponse,
  InfluencerPerformanceResponse,
  AdminInfluencerAnalyticsResponse,
  CommissionPayment,
  ReferralLink,
} from '@/types/influencer';

class InfluencerService {
  private baseUrl = '/influencer';

  // Personal Influencer Endpoints
  async generateReferralLink(request: GenerateReferralLinkRequest): Promise<GenerateReferralLinkResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/generate-link`, request);
      return response.data;
    } catch (error: any) {
      console.error('Error generating referral link:', error);
      throw error;
    }
  }

  async getDashboardData(): Promise<InfluencerPerformanceResponse> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/dashboard`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  async getPaymentHistory(): Promise<{ success: boolean; data: CommissionPayment[]; message?: string }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/payments`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  async requestPayout(amount: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/payout-request`, { amount });
      return response.data;
    } catch (error: any) {
      console.error('Error requesting payout:', error);
      throw error;
    }
  }

  async trackReferralClick(referralCode: string, metadata?: any): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/track-click`, {
        referralCode,
        metadata,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error tracking referral click:', error);
      throw error;
    }
  }

  // Admin Endpoints
  async getInfluencerAnalytics(): Promise<AdminInfluencerAnalyticsResponse> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/analytics`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching influencer analytics:', error);
      throw error;
    }
  }

  async getTopPerformers(): Promise<{ success: boolean; data: any[]; message?: string }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/top-performers`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching top performers:', error);
      throw error;
    }
  }

  async promoteUserToInfluencer(userId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/promote-user`, { userId });
      return response.data;
    } catch (error: any) {
      console.error('Error promoting user to influencer:', error);
      throw error;
    }
  }

  async processPayouts(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/process-payouts`);
      return response.data;
    } catch (error: any) {
      console.error('Error processing payouts:', error);
      throw error;
    }
  }

  async getConversionRates(): Promise<{ success: boolean; data: any; message?: string }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/conversion-rates`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching conversion rates:', error);
      throw error;
    }
  }

  async getGeographicSpread(): Promise<{ success: boolean; data: any; message?: string }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/geographic-spread`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching geographic spread:', error);
      throw error;
    }
  }

  async getROIAnalysis(): Promise<{ success: boolean; data: any; message?: string }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/roi-analysis`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching ROI analysis:', error);
      throw error;
    }
  }

  // Utility Methods
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      return false;
    }
  }

  generateQRCode(url: string): string {
    // Generate QR code URL using a service like QR Server
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  }

  generateSocialShareLinks(referralUrl: string, platform: string): string {
    const encodedUrl = encodeURIComponent(referralUrl);
    const encodedText = encodeURIComponent('Join WeTrippen and start your adventure! 🚀');
    
    switch (platform) {
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      case 'whatsapp':
        return `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
      default:
        return referralUrl;
    }
  }

  // Email Marketing Methods

  // Upload contact list
  async uploadContacts(contacts: Array<{ email: string; firstName?: string; lastName?: string; company?: string }>): Promise<{ success: boolean; data: any[]; message: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/email/upload-contacts`, { contacts });
      return response.data;
    } catch (error: any) {
      console.error('Error uploading contacts:', error);
      throw error;
    }
  }

  // Get contact list
  async getEmailContacts(page: number = 1, limit: number = 50): Promise<{ contacts: any[]; total: number; page: number; totalPages: number }> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/email/contacts`, {
        params: { page, limit },
      });
      return response.data.data;
    } catch (error: any) {
      console.error('Error getting contacts:', error);
      throw error;
    }
  }

  // Create campaign
  async createEmailCampaign(campaignData: { name: string; subject: string; content: string; scheduledAt?: string }): Promise<any> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/email/campaigns`, campaignData);
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  // Send campaign
  async sendEmailCampaign(campaignId: string, contactIds: string[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/email/campaigns/${campaignId}/send`, {
        contactIds,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error sending campaign:', error);
      throw error;
    }
  }

  // Get campaign statistics
  async getCampaignStats(campaignId: string): Promise<any> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/email/campaigns/${campaignId}/stats`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error getting campaign stats:', error);
      throw error;
    }
  }
}

export const influencerService = new InfluencerService(); 